import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatCurrency } from './radarUtils';

const AcoesPage = () => {
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [secao, setSecao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('impacto_desc');
  const [data, setData] = useState({ itens: [] });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/radar-lucro/acoes');
      setData(response.data || { itens: [] });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar acoes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const secoes = useMemo(() => {
    const set = new Set((data.itens || []).map((i) => i.secao).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data.itens]);

  const itensFiltrados = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    const base = (data.itens || []).filter((item) => {
      const matchBusca =
        !texto ||
        String(item.descricao || '').toLowerCase().includes(texto) ||
        String(item.codigoean || '').toLowerCase().includes(texto);
      const matchSecao = secao === 'todas' || (item.secao || '') === secao;
      return matchBusca && matchSecao;
    });

    const impacto = (item) => Number(item.risco_7d || item.valor_parado || item.impacto_30d || 0);
    const sorted = [...base];
    if (ordenacao === 'impacto_desc') sorted.sort((a, b) => impacto(b) - impacto(a));
    if (ordenacao === 'impacto_asc') sorted.sort((a, b) => impacto(a) - impacto(b));
    return sorted;
  }, [data.itens, busca, secao, ordenacao]);

  return (
    <div className="animate-fade-in space-y-4">
      <div className="rounded-xl border border-success-200 bg-success-50 p-4">
        <p className="text-sm font-semibold text-success-800">Plano de acao de hoje</p>
        <p className="text-sm text-success-800 mt-1">Priorize estas decisoes para proteger receita, reduzir capital parado e recuperar margem.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Acoes Recomendadas Hoje</h1>
          <p className="text-gray-600">Lista priorizada para proteger caixa e margem</p>
        </div>
        <button className="btn-secondary" onClick={loadData} disabled={loading}>
          Atualizar
        </button>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Buscar por descricao ou EAN" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="form-select" value={secao} onChange={(e) => setSecao(e.target.value)}>
            <option value="todas">Todas as secoes</option>
            {secoes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="form-select" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
            <option value="impacto_desc">Maior impacto</option>
            <option value="impacto_asc">Menor impacto</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">Itens filtrados: {itensFiltrados.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>EAN</th>
                <th>Produto</th>
                <th>Secao</th>
                <th>Motivo</th>
                <th>Impacto</th>
                <th>CTA</th>
              </tr>
            </thead>
            <tbody>
              {itensFiltrados.map((item, index) => (
                <tr key={`${item.tipo}-${item.id_produto}-${index}`}>
                  <td className="font-medium">{item.tipo}</td>
                  <td>{item.codigoean || '-'}</td>
                  <td>{item.descricao}</td>
                  <td>{item.secao || '-'}</td>
                  <td>{item.motivo}</td>
                  <td>{formatCurrency(item.risco_7d || item.valor_parado || item.impacto_30d || 0)}</td>
                  <td>
                    <Link to={item.cta} className="text-primary-700 font-medium text-sm">
                      Abrir e executar
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && itensFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-500 py-8">
                    Nenhuma acao prioritaria no momento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AcoesPage;
